/**
 * Amostrador de frame time (PERFORMANCE M3).
 *
 * Mede p95 e o pior 1%, NEVER a média: variância é o que o jogador sente como travada.
 *
 * Sem alocação: buffer circular e scratch pré-alocados no construtor.
 * `push` roda por frame — a regra `no-alloc-in-hot-path` guarda isso.
 */
export interface FrameStats {
  count: number;
  meanMs: number;
  p95Ms: number;
  worst1PercentMs: number;
  maxMs: number;
}

export class FrameSampler {
  private readonly samples: Float64Array;
  private readonly scratch: Float64Array;
  private readonly stats: FrameStats = {
    count: 0,
    meanMs: 0,
    p95Ms: 0,
    worst1PercentMs: 0,
    maxMs: 0,
  };
  private writeIndex = 0;
  private filled = 0;

  constructor(readonly capacity = 240) {
    if (capacity < 20) throw new Error('capacidade mínima é 20 amostras');
    this.samples = new Float64Array(capacity);
    this.scratch = new Float64Array(capacity);
  }

  /** Caminho quente: chamado uma vez por frame. NEVER aloque aqui. */
  push(frameMs: number): void {
    if (!Number.isFinite(frameMs) || frameMs < 0) return;
    this.samples[this.writeIndex] = frameMs;
    this.writeIndex = (this.writeIndex + 1) % this.capacity;
    if (this.filled < this.capacity) this.filled += 1;
  }

  get sampleCount(): number {
    return this.filled;
  }

  /** Há amostras suficientes para uma decisão de tier não ser ruído? */
  get isWarm(): boolean {
    return this.filled >= Math.min(this.capacity, 60);
  }

  reset(): void {
    this.writeIndex = 0;
    this.filled = 0;
  }

  /**
   * Estatísticas da janela. Chamado raramente (HUD a ≤ 10 Hz), não por frame.
   * Reutiliza o objeto de retorno de propósito: NEVER guarde a referência.
   */
  compute(): FrameStats {
    const n = this.filled;
    this.stats.count = n;
    if (n === 0) {
      this.stats.meanMs = 0;
      this.stats.p95Ms = 0;
      this.stats.worst1PercentMs = 0;
      this.stats.maxMs = 0;
      return this.stats;
    }

    let sum = 0;
    for (let i = 0; i < n; i++) {
      const value = this.samples[i] ?? 0;
      this.scratch[i] = value;
      sum += value;
    }
    // Ordena em fatia própria, sem alocar: subarray compartilha o buffer.
    this.scratch.subarray(0, n).sort();

    const p95Index = Math.min(n - 1, Math.ceil(n * 0.95) - 1);
    const worstStart = Math.max(0, n - Math.max(1, Math.round(n * 0.01)));

    let worstSum = 0;
    let worstCount = 0;
    for (let i = worstStart; i < n; i++) {
      worstSum += this.scratch[i] ?? 0;
      worstCount += 1;
    }

    this.stats.meanMs = sum / n;
    this.stats.p95Ms = this.scratch[p95Index] ?? 0;
    this.stats.worst1PercentMs = worstCount === 0 ? 0 : worstSum / worstCount;
    this.stats.maxMs = this.scratch[n - 1] ?? 0;
    return this.stats;
  }
}
